import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Silent-refresh-and-retry: the access_token cookie is short-lived. Without this,
// once it expires mid-session every API call 401s while the UI still thinks the
// user is logged in (blank/broken modules, "Uncaught 401" errors). On a 401 from
// any call other than /auth/login|refresh|me, try POST /auth/refresh once (uses
// the httpOnly refresh_token cookie) and retry the original request. If the
// refresh itself fails (refresh token also expired), force a clean redirect to
// /login instead of leaving the app in a half-authenticated broken state.
let refreshPromise = null;
let refreshFailedUntil = 0; // epoch ms — short cooldown to avoid redundant /auth/refresh calls

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    const url = config?.url || '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh');
    if (response?.status === 401 && config && !config._retried && !isAuthCall) {
      config._retried = true;
      if (Date.now() < refreshFailedUntil) {
        if (window.location.pathname !== '/login') window.location.href = '/login';
        return Promise.reject(error);
      }
      try {
        refreshPromise = refreshPromise || api.post('/auth/refresh');
        await refreshPromise;
        refreshPromise = null;
        return api(config);
      } catch {
        refreshPromise = null;
        refreshFailedUntil = Date.now() + 10000; // 10s cooldown before trying again
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Convert snake_case lob shape from backend to camelCase used by existing components.
export const normalizeLob = (lob) => ({
  ...lob,
  accentBg: lob.accent_bg,
  accentTc: lob.accent_tc,
  accentBgDark: lob.accent_bg_dark,
  accentTcDark: lob.accent_tc_dark,
  pipelineStages: (lob.pipeline_stages || []).map((s, i) => ({
    ...s,
    color: lob.accent,
  })),
  leadStages: (lob.lead_stages || []).map((s) => ({
    ...s,
    color: lob.accent,
  })),
  dealTypes: lob.deal_types || [],
});

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' · ');
  }
  return JSON.stringify(detail);
};

export const fmtMoney = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return '$' + (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return '$' + Math.round(num / 1_000) + 'K';
  return '$' + num.toLocaleString();
};
